import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useEffectOnce, usePathParam, useQuery, useViewport } from '@chilecompra/react-kit'
import { Button, Card, Typography } from '@chilecompra/react-kit/components'

import ProtectByProfile from '../../components/Security/ProtectByProfile'
import {
  DETAIL_INNOVATION_SECTION_OPTIONS,
  DETAIL_SECTION_OPTIONS,
  MEETING_ACTION,
  STATUS_OPTIONS_DICTIONARY,
  USER_TYPES
} from '../../configs/settings/constants'
import { isLocalEnv, REACT_APP_SELLER_BASE_URL, REACT_APP_UNIQUE_PASS_SEED } from '../../configs/settings/environment'
import routes from '../../configs/settings/routes'
import { useRecaptcha } from '../../modules/hooks/recaptcha'
import { useCsrfToken } from '../../modules/hooks/token'
import { updateQuery } from '../../modules/utils/searches'
import { useAuthProvider } from '../AuthProvider/AuthProvider.hook'
import { onShowExtendEndDateDialog } from '../RequestExtendEndDate/RequestExtendEndDate.actions'
import { onShowHistoryDialog } from '../RequestHistory/RequestHistory.actions'
import { onResetMeetingForm, onShowMeetingDialog } from '../RequestMeetingDialog/RequestMeetingDialog.actions'
import { onCleanPublicUser, onSignInUniqueKeyThunk } from '../UniquePassAuth/UniquePassAuth.actions'
import {
  onAddQuestionFileTempThunk,
  onDeleteMeetingThunk,
  onDownloadFileThunk,
  onDownloadQuestionsAndAnswersThunk,
  onGetCountriesThunk,
  onRemoveQuestionFileTempThunk,
  onRequestDetailInfoThunk,
  onResetDetail,
  onSendResponseThunk,
  onUploadFileThunk
} from './RequestDetailInfo.actions'
import HeadingDetailInfo from './RequestDetailInfo.heading'
import QuestionsAndAnswers from './RequestDetailInfo.questionsAndAnswers'
import SectionsDetail from './RequestDetailInfo.sections'
import { Container, Item, SellerLink, WrapperGrid } from './RequestDetailInfo.styles'
import SectionUploadAttachedFiles from './RequestDetailInfo.uploadAttachedFiles'

/**
 * The RequestDetailInfo's container.
 */
const RequestDetailInfo = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const query = useQuery()
  const { size } = useViewport()
  const csrfToken = useCsrfToken(REACT_APP_UNIQUE_PASS_SEED)
  const { canChangeRFI, isPrivate, userType } = useAuthProvider()
  const code = usePathParam('code')
  const { executeRecaptcha } = useRecaptcha('rfi_detail_info')

  const {
    authInfo: { orgCode: authProviderOrgCode }
  } = useSelector(state => state.authProvider)
  const { publicUserDni, publicUserName } = useSelector(state => state.uniquePassAuth)
  const {
    additionalInfo,
    additionalInfoUrls,
    antecedents,
    attachedFiles,
    countries,
    description,
    descriptionProblem,
    endDate,
    entCode,
    error,
    errorAttachedFiles,
    errorCountries,
    errorDownloading,
    histories,
    historyProblem,
    isDownloading,
    isInnovation,
    isInvestigated,
    loading,
    loadingAttachedFiles,
    loadingCountries,
    loadingMeetings,
    locationProblem,
    meetings,
    name,
    orgCode,
    peopleAffected,
    products,
    publicationDate,
    questions,
    reason,
    status,
    usrCode,
    willBePublic,
    willBeRequiredConcept
  } = useSelector(state => state.requestDetailInfo)
  const { filters } = useSelector(state => state.requestList)

  const handleDownloadFile = useCallback(
    file =>
      dispatch(
        onDownloadFileThunk({
          ...file
        })
      ),
    []
  )

  const handleResponse = useCallback(response => {
    dispatch(onSendResponseThunk({ ...response, executeRecaptcha }))
  }, [])

  const handleShowHistoryDialog = useCallback(() => dispatch(onShowHistoryDialog()), [])

  const handleSignedUniquePass = () => {
    dispatch(
      onSignInUniqueKeyThunk({
        csrfToken
      })
    )
  }

  const handleUploadFile = useCallback(
    file =>
      dispatch(
        onUploadFileThunk({
          file
        })
      ),
    []
  )

  const handleAddFile = useCallback(({ file, questionId }) => {
    dispatch(
      onAddQuestionFileTempThunk({
        file,
        questionId
      })
    )
  }, [])

  const handleDeleteMeeting = useCallback(meeting => {
    dispatch(
      onDeleteMeetingThunk({
        meeting,
        reloadDetailCallback: () =>
          dispatch(
            onRequestDetailInfoThunk({
              code
            })
          )
      })
    )
  }, [])

  const handleDownloadQuestionsAndAnswers = useCallback(() => {
    dispatch(onDownloadQuestionsAndAnswersThunk())
  }, [])

  const handleGoBackWithParams = () => {
    updateQuery({
      filters,
      query
    })
    navigate(
      {
        pathname: isPrivate ? routes.search : routes.searchPublic,
        search: query.toString()
      },
      {
        replace: true
      }
    )
  }

  const handleRemoveFile = useCallback(({ fileId, questionId }) => {
    dispatch(
      onRemoveQuestionFileTempThunk({
        fileId,
        questionId
      })
    )
  }, [])

  const handleShowExtendEndDateDialog = useCallback(() => {
    dispatch(
      onShowExtendEndDateDialog({
        dateTime: endDate
      })
    )
  }, [endDate])

  const handleShowMeetingDialog = useCallback(() => {
    batch(() => {
      dispatch(onResetMeetingForm())
      dispatch(
        onShowMeetingDialog({
          action: MEETING_ACTION.DETAIL_INFO
        })
      )
    })
  }, [])

  const detailSectionOptions = useMemo(() => {
    if (isInnovation) return DETAIL_INNOVATION_SECTION_OPTIONS

    return DETAIL_SECTION_OPTIONS
  }, [DETAIL_SECTION_OPTIONS, DETAIL_INNOVATION_SECTION_OPTIONS, isInnovation])

  /* Initial request detail */
  useEffectOnce(() => {
    if (!isLocalEnv && publicUserDni && publicUserName) dispatch(onCleanPublicUser())

    dispatch(
      onRequestDetailInfoThunk({
        code
      })
    )
  })

  useEffect(() => {
    if (!loading && userType === USER_TYPES.BUYER && authProviderOrgCode !== orgCode?.id) {
      batch(() => {
        dispatch(onResetDetail())
        handleGoBackWithParams()
      })
    }
  }, [loading])

  useEffectOnce(() => {
    if (countries.length > 0) return

    dispatch(onGetCountriesThunk())
  })

  if (!loading && userType === USER_TYPES.BUYER && authProviderOrgCode !== orgCode?.id) return

  return (
    <Container margin="0 0 0 -32px" padding="16px 0" spacing={4}>
      <HeadingDetailInfo code={code} isError={!!error} isLoading={loading} status={status} />

      <Item md={9} order={size.isDownToLarge ? 3 : 2} xs={12}>
        {!error && (
          <Container>
            {detailSectionOptions.map(section => (
              <WrapperGrid key={section.name} padding="18px 0">
                <Typography fontWeight="bold" variant="body2">
                  {section.title}
                </Typography>

                <SectionsDetail
                  additionalInfo={additionalInfo}
                  additionalInfoUrls={additionalInfoUrls}
                  antecedents={antecedents}
                  attachedFiles={attachedFiles}
                  description={description}
                  descriptionProblem={descriptionProblem}
                  endDate={endDate}
                  histories={histories}
                  historyProblem={historyProblem}
                  isDisabled={loading}
                  isInnovation={isInnovation}
                  isInvestigated={isInvestigated}
                  isLoading={loading}
                  loadingAttachedFiles={loadingAttachedFiles}
                  loadingMeetings={loadingMeetings}
                  locationProblem={locationProblem}
                  meetings={meetings}
                  name={name}
                  onDeleteMeeting={handleDeleteMeeting}
                  onDownloadFile={handleDownloadFile}
                  onShowExtendEndDateDialog={handleShowExtendEndDateDialog}
                  onShowHistoryDialog={handleShowHistoryDialog}
                  onShowMeetingDialog={handleShowMeetingDialog}
                  peopleAffected={peopleAffected}
                  products={products}
                  publicationDate={publicationDate}
                  reason={reason}
                  sectionName={section.name}
                  status={status}
                  willBeRequiredConcept={willBeRequiredConcept}
                />
              </WrapperGrid>
            ))}

            <ProtectByProfile
              rule={userType =>
                status !== STATUS_OPTIONS_DICTIONARY.CLOSED && userType === USER_TYPES.BUYER && canChangeRFI
              }
            >
              <WrapperGrid padding="18px 0">
                <Item>
                  <Typography fontWeight="bold" margin="0 0 8px" variant="body2">
                    Adjuntar archivos
                  </Typography>

                  <Typography variant="subtitle">
                    Puedes subir archivos que especifiquen tu solicitud (opcional).
                  </Typography>
                </Item>

                <SectionUploadAttachedFiles
                  isError={!!errorAttachedFiles}
                  isLoading={loading || loadingAttachedFiles}
                  onUploadFile={handleUploadFile}
                />
              </WrapperGrid>
            </ProtectByProfile>
          </Container>
        )}
      </Item>

      {!error && !loading && (
        <Item lg={3} md={9} order={size.isDownToLarge ? 2 : 3} xs={12}>
          <Card padding="24px">
            <Container direction="column" justifyContent="center">
              <Typography fontWeight="bold" margin="0 0 16px" variant="h4">
                Datos para esta consulta
              </Typography>

              <Typography margin="0 0 8px" variant="body2">
                <SellerLink href={`${REACT_APP_SELLER_BASE_URL}/ficha/${entCode.dni}`} target="_blank">
                  {entCode.name}
                </SellerLink>
              </Typography>

              <Typography margin="0 0 8px" variant="body2">
                {entCode.dni}
              </Typography>

              <Typography variant="subtitle">{orgCode.name}</Typography>
            </Container>
          </Card>
        </Item>
      )}

      <QuestionsAndAnswers
        countries={countries}
        detailUsrCode={usrCode}
        errorCountries={errorCountries}
        isClose={status === STATUS_OPTIONS_DICTIONARY.CLOSED}
        isDownloading={isDownloading}
        isError={!!error}
        isErrorDownloading={errorDownloading}
        isLoading={loading}
        isWillBePublic={willBePublic}
        loadingCountries={loadingCountries}
        onAddFile={handleAddFile}
        onDownloadFile={handleDownloadFile}
        onDownloadQuestionsAndAnswers={handleDownloadQuestionsAndAnswers}
        onRemoveFile={handleRemoveFile}
        onResponse={handleResponse}
        onSignedUniquePass={handleSignedUniquePass}
        publicUserDni={publicUserDni}
        publicUserName={publicUserName}
        questions={questions}
      />

      <Item order={5} xs={12}>
        <Container>
          <Button disabled={loading || loadingAttachedFiles} onClick={handleGoBackWithParams} variant="outlined">
            Volver
          </Button>
        </Container>
      </Item>
    </Container>
  )
}

export default RequestDetailInfo
